import { Suspense, useEffect, useRef } from "react"
import { Outlet, useLocation } from "react-router-dom"
import { Sidebar } from "./sidebar/Sidebar"
import { Header } from "./header/Header"
import { SidebarCollapseProvider, useSidebarCollapse } from "../contexts/SidebarCollapseContext"
import { useInboxNotifications } from "../contexts/InboxNotificationContext"
import NotificationDrawer from "./ai-community-inbox/components/NotificationDrawer"
import FloatingUploadManager from "./AIAssetManager/FloatingUploadManager"
import SuccessToastHost from "./common/success-toast/SuccessToastHost"
import { markInboxHotelFilterResetOnReturn } from "./ai-community-inbox/utils/inboxHotelFilter"
import { prefetchRoute } from "../routes/lazyPages"

function RouteLazyFallback() {
  return <div className="route-lazy-fallback" role="status" aria-label="Loading" />
}

function LayoutContent() {
  const { forceCollapsed, setForceCollapsed } = useSidebarCollapse()
  const location = useLocation()
  const prevPathRef = useRef(null)
  const inboxCtx = useInboxNotifications()
  const isInboxRoute = location.pathname.includes('ai-community-inbox')
  const showNotificationPanel = isInboxRoute && inboxCtx?.drawerOpen
  const propertyPanelExpanded = isInboxRoute && inboxCtx?.propertyPanelExpanded

  useEffect(() => {
    prefetchRoute("/dashboard")
    prefetchRoute("/multi-property-dashboard")
  }, [])

  useEffect(() => {
    setForceCollapsed(showNotificationPanel || propertyPanelExpanded)
  }, [showNotificationPanel, propertyPanelExpanded, setForceCollapsed])

  /** Leaving Community AI Inbox → reset hotel filter to All on next visit (not on F5 refresh). */
  useEffect(() => {
    const prev = prevPathRef.current
    const curr = location.pathname
    if (prev != null) {
      const leftInbox =
        prev.includes("ai-community-inbox") && !curr.includes("ai-community-inbox")
      if (leftInbox) {
        markInboxHotelFilterResetOnReturn()
      }
    }
    prevPathRef.current = curr
  }, [location.pathname])

  return (
    <div className="app-layout">
      <Header />
      <div className={`content-area ${showNotificationPanel ? 'content-area--with-notifications' : ''}`}>
        <Sidebar />
        <div className="main-content">
          <Suspense fallback={<RouteLazyFallback />}>
            <Outlet />
          </Suspense>
        </div>
        {showNotificationPanel && (
          <div className="layout-notification-panel">
            <NotificationDrawer
              isOpen
              onClose={() => inboxCtx?.setDrawerOpen(false)}
              notifications={inboxCtx?.notifications ?? []}
              onSelectThread={inboxCtx?.selectThread}
              onClearNotifications={inboxCtx?.clearNotifications}
              pushLayout
            />
          </div>
        )}
      </div>
      <FloatingUploadManager />
      <SuccessToastHost />
    </div>
  )
}

const Layout = () => {
  return (
    <SidebarCollapseProvider>
      <LayoutContent />
    </SidebarCollapseProvider>
  )
}

export default Layout
